import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/components/ui/tooltip";
import { SelectCity } from "@/features/settings/citys/SelectCity";
import ImportedPackageActions from "./ImportedPackageActions";
import { Textarea } from "@shared/components/ui/textarea";

export function PackageImportedCard({ initialOrder, hasError }) {
  const { t } = useTranslation();
  const [order, setOrder] = useState(initialOrder);
  useEffect(() => {
    setOrder(initialOrder);
  }, [initialOrder]);
  const handleInputChange = (field, value) => {
    setOrder((prevOrder) => {
      const updateNestedField = (obj, keys, value) => {
        if (keys.length === 1) {
          return {
            ...obj,
            [keys[0]]: value,
          };
        }
        const [key, ...restKeys] = keys;

        // Handle array index if the key represents an array element
        if (key.match(/^\[\d+\]$/)) {
          const index = parseInt(key.slice(1, -1), 10); // Extract the array index
          const array = Array.isArray(obj) ? obj : []; // Ensure it's an array
          const updatedArray = [...array]; // Create a shallow copy of the array
          updatedArray[index] = updateNestedField(
            array[index] || {},
            restKeys,
            value
          ); // Update the specific index
          return updatedArray; // Return the updated array
        }

        return {
          ...obj,
          [key]: updateNestedField(obj[key] || {}, restKeys, value),
        };
      };

      const keys = field.split(".");

      return {
        ...prevOrder,
        data: updateNestedField(prevOrder.data, keys, value),
      };
    });
  };

  return (
    <Card key={order._id} className="w-full">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4 w-full">
          {/* Status and Error Section */}
          <div className="flex items-center gap-2">
            {order.errors.length > 0 ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <ul className="list-disc pl-4">
                      {order.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-green-500">✅</span>
            )}
          </div>

          {/* Order Details */}

          <div className="flex w-full items-center gap-4 flex-wrap">
            <div className="flex flex-col">
              <Label className="text-xs mb-1">
                {t("import.trackingNumber")}
              </Label>
              <Input
                value={order.data.trackingNumber}
                onChange={(e) =>
                  handleInputChange("trackingNumber", e.target.value)
                }
                className={`w-32 ${
                  hasError(order, "trackingNumber") ? "border-red-500" : ""
                }`}
                placeholder={t("import.trackingNumber")}
              />
            </div>

            <div className="flex flex-col">
              <Label className="text-xs mb-1">{t("import.customerName")}</Label>
              <Input
                value={order.data.customer.name}
                onChange={(e) =>
                  handleInputChange("customer.name", e.target.value)
                }
                className={`w-40 ${
                  hasError(order, "customer.name") ? "border-red-500" : ""
                }`}
                placeholder={t("import.customerName")}
              />
            </div>
            <div className="flex flex-col">
              <Label className="text-xs mb-1">{t("import.phone")}</Label>
              <Input
                value={order.data.customer.phone}
                onChange={(e) =>
                  handleInputChange("customer.phone", e.target.value)
                }
                className={`w-32 ${
                  hasError(order, "customer.phone") ? "border-red-500" : ""
                }`}
                placeholder={t("import.phone")}
              />
            </div>

            <div className="flex flex-col">
              <Label className="text-xs mb-1">{t("import.address")}</Label>
              <Input
                type="text"
                value={order.data.customer.address}
                onChange={(e) =>
                  handleInputChange("customer.address", e.target.value)
                }
                className={`w-24 ${
                  hasError(order, "customer.address") ? "border-red-500" : ""
                }`}
                placeholder={t("import.customerAddress")}
              />
            </div>
            <div className={`flex flex-col`}>
              <Label
                className={`text-xs mb-1 ${
                  hasError(order, "city") ? "text-red-500" : ""
                }`}
              >
                {t("import.city")} ({initialOrder.data.customer.city})
              </Label>
              <SelectCity
                city={order.data.customer.city}
                onSelectCity={(city) =>
                  handleInputChange("customer.city", city.name)
                }
              />
            </div>
            <div className="flex flex-col">
              <Label className="text-xs mb-1">{t("import.totalAmount")}</Label>
              <Input
                type="number"
                value={order.data.totalAmount}
                onChange={(e) =>
                  handleInputChange("totalAmount", e.target.value)
                }
                className={`w-24 ${
                  hasError(order, "totalAmount") ? "border-red-500" : ""
                }`}
                placeholder={t("import.amount")}
              />
            </div>
            <div className="flex flex-col">
              <Label className="text-xs mb-1">{t("import.productName")}</Label>
              <Input
                value={order.data.products[0].name}
                onChange={(e) =>
                  handleInputChange("products.[0].name", e.target.value)
                }
                className={`w-32 ${
                  hasError(order, "products[0].name") ? "border-red-500" : ""
                }`}
                placeholder={t("import.productName")}
              />
            </div>
            <div className="flex flex-col">
              <Label className="text-xs mb-1">
                {t("import.productQuantity")}
              </Label>
              <Input
                type="number"
                value={order.data.products[0].quantity}
                onChange={(e) =>
                  handleInputChange("products.[0].quantity", e.target.value)
                }
                className={`w-24 ${
                  hasError(order, "products.quantity") ? "border-red-500" : ""
                }`}
              />
            </div>
            <div className="flex flex-col">
              <Label className="text-xs mb-1">{t("common.note")}</Label>
              <Textarea
                value={order.data.note}
                onChange={(e) => handleInputChange("note", e.target.value)}
                className={`w-32 ${
                  hasError(order, "note") ? "border-red-500" : ""
                }`}
                placeholder={t("common.note")}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center">
            <ImportedPackageActions id={order._id} packageData={order} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
